import React, { useState } from 'react';
import api from '../../services/api';
import { useToast } from '../shared/ToastNotification';

interface QuickRegistrationProps {
    initialName?: string;
    onSuccess: (vendor: any) => void;
    onCancel: () => void;
}

const QuickRegistration: React.FC<QuickRegistrationProps> = ({ 
    initialName = '', 
    onSuccess, 
    onCancel 
}) => {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [newVendor, setNewVendor] = useState({
        name: initialName,
        phone: '',
        email: '',
        gstin: '',
        address: ''
    });
    const [errors, setErrors] = useState<string[]>([]);

    const handleCreateVendor = async () => {
        if (!newVendor.name.trim()) {
            toast.error('Name is required');
            return;
        }

        setLoading(true);
        setErrors([]);

        try {
            const response = await api.post('/search/quick_create_vendor', { 
                name: newVendor.name,
                phone: newVendor.phone,
                email: newVendor.email,
                gstin: newVendor.gstin,
                address: newVendor.address
            });

            if (response.success) {
                const vendorData = response.data?.vendor || response.data || response.vendor;
                
                toast.success('Vendor registered successfully!');
                onSuccess(vendorData);
            } else {
                const backendErrors = response.errors || [response.message || 'Failed to create vendor'];
                setErrors(Array.isArray(backendErrors) ? backendErrors : [backendErrors]);
                toast.error(backendErrors[0]);
            }
        } catch (err: any) {
            console.error('Registration failed:', err);
            toast.error('An unexpected error occurred during registration');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-slate-50 border border-indigo-100 rounded-2xl space-y-4 animate-in slide-in-from-top-2 duration-300 shadow-inner shadow-slate-200/50">
            <h4 className="text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                Rapid Registration
            </h4>
            
            {errors.length > 0 && (
                <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg text-rose-600 text-[10px] font-bold">
                    {errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
            )}

            <div className="grid grid-cols-1 gap-3">
                <input
                    type="text"
                    value={newVendor.name}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm"
                    placeholder="Legal Name"
                    disabled={loading}
                    autoFocus
                />
                <div className="grid grid-cols-2 gap-3">
                    <input
                        type="tel"
                        value={newVendor.phone}
                        onChange={(e) => setNewVendor(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm"
                        placeholder="Mobile Number"
                        disabled={loading}
                    />
                    <input
                        type="text"
                        value={newVendor.gstin}
                        onChange={(e) => setNewVendor(prev => ({ ...prev, gstin: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm uppercase"
                        placeholder="GST Number (Optional)"
                        disabled={loading}
                    />
                </div>
                <input
                    type="text"
                    value={newVendor.address}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm"
                    placeholder="Full Address"
                    disabled={loading}
                />
                <input
                    type="email"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-sm"
                    placeholder="Email Address"
                    disabled={loading}
                />
            </div>

            <div className="flex gap-2 pt-2">
                <button
                    type="button"
                    onClick={handleCreateVendor}
                    disabled={loading}
                    className="flex-1 py-3 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 active:scale-[0.98] disabled:opacity-50"
                >
                    {loading ? 'Processing...' : 'Confirm Registration'}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={loading}
                    className="px-6 py-3 bg-white border border-slate-200 text-slate-500 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default QuickRegistration;
